import {
  Box,
  Center,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { graphql, RelaySafeProvider, useFragment } from 'utils/graphClient';
import { EditForm } from './screens/EditForm';
import { RespondForm } from './screens/RespondForm';
import { FormViewer_node$key } from './__generated__/FormViewer_node.graphql';
import { CloseConfirmationDialog } from '../PageEditor/components/CloseConfirmationDialog';
import { FormResponses } from './screens/FormResponses';

type FormViewerScreen = 'respond' | 'edit' | 'responses';

const SCREEN_ORDER: FormViewerScreen[] = ['respond', 'edit', 'responses'];

const fragment = graphql`
  fragment FormViewer_node on Node {
    ... on FolderItem {
      id
      publishState
      ...RespondForm_folderItem
      ...EditForm_folderItem
      canUpdate {
        value
      }
      content {
        ... on Form {
          id
          isClosed
          ...RespondForm_form
          ...EditForm_form
          responses {
            totalCount
          }
        }
      }
      ...PublishReminderDialog_folderItem
    }
  }
`;

interface FormViewerProps {
  node: FormViewer_node$key;
}

export const FormViewer = ({ node }: FormViewerProps) => {
  const { id, canUpdate, publishState, content, ...folderItem } = useFragment(
    fragment,
    node,
  );

  const canEdit = canUpdate?.value;
  const isDraft = publishState === 'DRAFT';

  const { currentScreen, setCurrentScreen } = useFormViewerScreen({
    canEdit,
    isDraft,
  });
  const currentIndex = SCREEN_ORDER.findIndex(
    (screen) => screen === currentScreen,
  );

  if (!id || !content) {
    throw new Error('Invalid form');
  }

  if (!canUpdate?.value) {
    return <RespondForm folderItem={folderItem} form={content} />;
  }

  const amountOfResponses = content?.responses?.totalCount || 0;

  return (
    <>
      <Tabs
        data-qa="form-viewer"
        variant="soft-rounded"
        isLazy
        index={currentIndex}
        onChange={(index) => setCurrentScreen(SCREEN_ORDER[index])}
        flex={1}
        py={2}
        overflow="auto"
      >
        <TabList justifyContent="center">
          <Tab _selected={{ color: 'white', bg: 'blue.500' }}>{t`Respond`}</Tab>
          {canEdit && (
            <Tab _selected={{ color: 'white', bg: 'blue.500' }}>{t`Edit`}</Tab>
          )}
          {canEdit && (
            <Tab _selected={{ color: 'white', bg: 'blue.500' }}>
              {t`Responses (${amountOfResponses})`}
            </Tab>
          )}
        </TabList>
        <TabPanels>
          <TabPanel>
            <RespondForm folderItem={folderItem} form={content} />
          </TabPanel>
          <TabPanel>
            {content && <EditForm folderItem={folderItem} form={content} />}
          </TabPanel>

          <TabPanel>
            <RelaySafeProvider
              LoadingComponent={() => (
                <Box>
                  <Center>
                    <Spinner />
                  </Center>
                  <Center>{t`Loading responses`}</Center>
                </Box>
              )}
              ErrorComponent={() => <Box>{t`Error loading responses`}</Box>}
            >
              <FormResponses nodeId={id} />
            </RelaySafeProvider>
          </TabPanel>
        </TabPanels>
      </Tabs>

      <CloseConfirmationDialog node={folderItem} isDraft={isDraft} />
    </>
  );
};

const FORM_VIEWER_SCREEN_PARAM_NAME = 'formscreen';

interface useFormViewerScreenProps {
  canEdit?: boolean;
  isDraft?: boolean;
}

export function useFormViewerScreen({
  canEdit,
  isDraft,
}: useFormViewerScreenProps): {
  currentScreen: FormViewerScreen;
  setCurrentScreen(screen: FormViewerScreen): void;
} {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const screenParam = params.get(FORM_VIEWER_SCREEN_PARAM_NAME);
  const defaultScreen = !canEdit ? 'respond' : isDraft ? 'edit' : 'responses';
  const currentScreen = isFormViewerScreen(screenParam)
    ? screenParam
    : defaultScreen;
  return {
    currentScreen,
    setCurrentScreen: (currentScreen: FormViewerScreen) => {
      params.set(FORM_VIEWER_SCREEN_PARAM_NAME, currentScreen);
      navigate({ search: params.toString() });
    },
  };
}

function isFormViewerScreen(screen: any): screen is FormViewerScreen {
  switch (screen) {
    case 'respond':
    case 'edit':
    case 'responses':
      return true;
    default:
      return false;
  }
}
